import React from "react";
import { Link } from "react-router-dom";
import { Droplets } from "lucide-react";

/**
 * Public privacy policy. Lives at /privacy so we can paste the URL into the
 * Plaid production questionnaire (and any other compliance form). Tailored
 * to the actual data flow of this app: single-owner admin, customer portal,
 * driver app, and the Plaid read-only credit-card import.
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold tracking-tight">
            <Droplets className="w-5 h-5 text-primary" /> Catskills Pool Supplies
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="privacy-login-link">Sign in</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6 leading-relaxed" data-testid="privacy-page">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Privacy Policy</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">How we handle your data</h1>
          <p className="text-sm text-muted-foreground mt-2" data-testid="privacy-updated-date">Last updated: October 2026</p>
        </div>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Who we are</h2>
          <p>
            This service (the &quot;Service&quot;) is operated by <strong>Catskills Pool Supplies LLC</strong>
            {" "}(&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), a New York limited liability company that delivers bulk chlorine
            and pool supplies to customers in the Catskills region. You can reach us at
            {" "}<a href="mailto:catskillspool@gmail.com" className="text-primary underline">catskillspool@gmail.com</a>.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">What this policy covers</h2>
          <p>
            This policy describes what information we collect through the customer portal at
            {" "}<span className="font-mono">catskillspoolsupplies.com</span>, our internal admin and driver applications,
            and the third-party services we use to run our business.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Information we collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account information</strong> — name, email address, hashed password, and (if you choose) phone number.</li>
            <li><strong>Service address &amp; pool details</strong> — your delivery address, pool name, capacity in gallons, entry instructions (e.g. gate code), and optional photos of your pool to help our driver locate it.</li>
            <li><strong>Order and delivery history</strong> — gallons delivered, dates, invoices, payment status.</li>
            <li><strong>Payment information</strong> — when you save a card, we receive only a non-reversible token from our payment processor (Cardknox). We never see or store your card number, CVV, or expiration date.</li>
            <li><strong>Device information</strong> — browser type, IP address, and a per-device token used to recognize trusted devices for sign-in.</li>
            <li data-testid="privacy-management-usage"><strong>Management system usage</strong> — last active time, active days, visits, estimated focused time in Management/Worker/Colony screens, and counts of saved work. Usage summaries are visible only to the system owner. Time is sampled in 30-second intervals and excludes hidden, unfocused or idle screens. We do not record keystrokes, form contents, screen recordings or off-site browsing.</li>
            <li><strong>Business banking data (owner only)</strong> — when the business owner connects a credit card or bank account through Plaid for expense tracking, Plaid returns transaction records (date, amount, merchant, category) to our internal Finance module. This data is for the owner&apos;s use only and is never shared with customers, drivers, or any third party.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">How we use your information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To schedule and fulfill chlorine and pool-supply deliveries.</li>
            <li>To generate invoices, accept payments, and email receipts.</li>
            <li>To send delivery, payment, and account-security notifications via email and SMS.</li>
            <li>To route our driver to your pool, with optional ETA tracking.</li>
            <li>To protect the account from unauthorized access (failed-login lockouts, new-device verification codes).</li>
            <li>To run the owner&apos;s internal bookkeeping (Finance module — owner only).</li>
          </ul>
          <p className="text-sm text-muted-foreground">We do <strong>not</strong> sell, rent, or trade your personal information to anyone.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Third-party services we rely on</h2>
          <table className="w-full text-sm border border-border">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Provider</th><th className="text-left px-3 py-2">Purpose</th><th className="text-left px-3 py-2">Data shared</th></tr>
            </thead>
            <tbody>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Cardknox</td><td className="px-3 py-2">Credit-card processing</td><td className="px-3 py-2">Card details (tokenized in your browser, we never receive raw card data)</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Plaid</td><td className="px-3 py-2">Read-only bank/card transaction import — owner only</td><td className="px-3 py-2">The owner&apos;s business bank credentials are entered on the bank&apos;s own site; we only ever receive an access token + transaction history</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Resend</td><td className="px-3 py-2">Transactional email (invoices, receipts, security codes)</td><td className="px-3 py-2">Recipient email + email body</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Twilio</td><td className="px-3 py-2">Driver / delivery SMS (when enabled)</td><td className="px-3 py-2">Recipient phone + message body</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Google (OAuth)</td><td className="px-3 py-2">Optional &quot;Sign in with Google&quot;</td><td className="px-3 py-2">Your Google email + profile name</td></tr>
              <tr className="border-t border-border"><td className="px-3 py-2 font-semibold">Cloudflare</td><td className="px-3 py-2">DNS, HTTPS, and DDoS protection</td><td className="px-3 py-2">Standard web traffic metadata</td></tr>
            </tbody>
          </table>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">How we secure your data</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>All traffic is encrypted in transit using TLS 1.2+ (HTTPS).</li>
            <li>Account passwords are hashed with bcrypt — we cannot recover or read them.</li>
            <li>Database storage is encrypted at rest (AES-256).</li>
            <li>Production data is accessible only to the business owner. Driver and secretary accounts have strictly limited views.</li>
            <li>Multi-factor authentication is enforced via an email one-time code on any new device.</li>
            <li>Accounts auto-lock after repeated failed sign-in attempts.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Data retention &amp; deletion</h2>
          <p>
            We keep your account and order history for as long as you remain a customer. You can ask us to delete your
            account and personal information at any time by emailing
            {" "}<a href="mailto:catskillspool@gmail.com" className="text-primary underline">catskillspool@gmail.com</a>.
            Invoice and tax records may be retained for the legally required period (typically 7 years) for accounting and tax compliance.
          </p>
          <p>
            When the owner disconnects a Plaid-connected card from the Finance module, the Plaid access token is permanently deleted within seconds.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Your rights</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong>Correction</strong> — fix anything that is wrong or out of date.</li>
            <li><strong>Deletion</strong> — request that we erase your account and data (subject to the tax-records exception above).</li>
            <li><strong>Withdraw consent</strong> — revoke a stored credit card, turn off auto-charge, or close your portal account at any time.</li>
          </ul>
          <p>To exercise any of these rights, email <a href="mailto:catskillspool@gmail.com" className="text-primary underline">catskillspool@gmail.com</a>.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Children</h2>
          <p>This Service is intended for adults running or managing pool maintenance for residential or commercial properties. We do not knowingly collect data from anyone under 13.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Changes to this policy</h2>
          <p>If we make meaningful changes, we will update the &quot;Last updated&quot; date at the top of this page and, where appropriate, notify you by email.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-2xl font-bold">Questions?</h2>
          <p>Email <a href="mailto:catskillspool@gmail.com" className="text-primary underline">catskillspool@gmail.com</a> and we&apos;ll respond within a few business days.</p>
        </section>

        <footer className="pt-8 border-t border-border text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-3">
          <span>© Catskills Pool Supplies LLC</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-foreground">Terms of service</Link>
            <Link to="/" className="hover:text-foreground">Home</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
